/**
 * PreviewRenderer - 실시간 React 코드 미리보기 렌더러
 *
 * 기능:
 * - JSX/TSX 코드를 실시간으로 렌더링
 * - ESBuild WASM으로 트랜스파일
 * - 격리된 iframe 샌드박스
 * - 에러 표시
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  transpileCode,
  buildPreviewHtml,
  parseTranspileError,
  initializeEsbuild,
} from './TranspilerService';

export interface PreviewRendererProps {
  /** 렌더링할 코드 */
  code: string;
  /** 언어 */
  language?: 'jsx' | 'tsx';
  /** 테마 */
  theme?: 'light' | 'dark';
  /** 디바이스 너비 */
  width?: string;
  /** 클래스명 */
  className?: string;
  /** 에러 발생 시 콜백 */
  onError?: (error: { line?: number; column?: number; message: string } | null) => void;
  /** 트랜스파일 성공 시 콜백 */
  onSuccess?: () => void;
}

export function PreviewRenderer({
  code,
  language = 'tsx',
  theme = 'light',
  width = '100%',
  className,
  onError,
  onSuccess,
}: PreviewRendererProps) {
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // ESBuild 초기화
  useEffect(() => {
    initializeEsbuild()
      .then(() => {
        setIsInitialized(true);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(`ESBuild 초기화 실패: ${err.message}`);
        setIsLoading(false);
      });
  }, []);

  // 코드 트랜스파일 및 미리보기 생성
  const updatePreview = useCallback(async () => {
    if (!isInitialized || !code.trim()) {
      setPreviewHtml('');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await transpileCode(code, language);

      if (!result.success) {
        const parsedError = parseTranspileError(result.error || '알 수 없는 에러');
        setError(result.error || '트랜스파일 실패');
        onError?.(parsedError);
        setIsLoading(false);
        return;
      }

      const html = buildPreviewHtml(result.code!, {
        title: 'Preview',
        theme,
        tailwindEnabled: true,
      });

      setPreviewHtml(html);
      setError(null);
      onError?.(null);
      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      onError?.({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  }, [code, language, theme, isInitialized, onError, onSuccess]);

  // 디바운스된 업데이트
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      updatePreview();
    }, 500); // 500ms 디바운스

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [updatePreview]);

  // 수동 새로고침
  const handleRefresh = () => {
    updatePreview();
  };

  // 초기화 중
  if (!isInitialized && isLoading) {
    return (
      <div
        className={cn(
          'flex h-full items-center justify-center bg-muted/30',
          className
        )}
      >
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">미리보기 엔진 초기화 중...</p>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div
        className={cn(
          'flex h-full flex-col items-center justify-center bg-red-50 p-6',
          className
        )}
      >
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <div className="rounded-full bg-red-100 p-3">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <div>
            <h3 className="font-semibold text-red-700">컴파일 에러</h3>
            <p className="mt-2 font-mono text-sm text-red-600">{error}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // 빈 코드
  if (!code.trim()) {
    return (
      <div
        className={cn(
          'flex h-full items-center justify-center bg-muted/30',
          className
        )}
      >
        <p className="text-muted-foreground">코드를 입력하면 미리보기가 표시됩니다</p>
      </div>
    );
  }

  return (
    <div className={cn('relative h-full', className)}>
      {/* 로딩 오버레이 */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {/* 미리보기 iframe */}
      <iframe
        ref={iframeRef}
        srcDoc={previewHtml}
        className="h-full w-full border-0"
        title="프로토타입 미리보기"
        sandbox="allow-scripts"
        style={{ width }}
      />

      {/* 새로고침 버튼 */}
      <button
        onClick={handleRefresh}
        className="absolute bottom-4 right-4 z-20 rounded-full bg-background/80 p-2 shadow-lg backdrop-blur-sm hover:bg-background transition-colors"
        title="새로고침"
      >
        <RefreshCw className="h-4 w-4" />
      </button>
    </div>
  );
}
